if (!window.Eurus.loadedScript.has('lp-buy-box.js')) {
  window.Eurus.loadedScript.add('lp-buy-box.js');

  const LOOP_API = 'https://api.loopsubscriptions.com/storefront/2023-10/bundle';
  const PURCHASE_TYPES = ['subscription', 'one-time'];
  const SUCCESS_DELAY_MS = 550;
  const bundleCache = new Map();

  const interpolate = (template, values = {}) =>
    String(template ?? '').replace(/\[(\w+)\]/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
    );

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const routeRoot = () => window.Shopify?.routes?.root ?? '/';

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const savingsPercent = ({ currentCents, compareCents }) =>
    compareCents > currentCents ? Math.round(((compareCents - currentCents) / compareCents) * 100) : 0;

  const readJson = async (response) => {
    const contentType = response.headers.get('content-type') ?? '';
    return contentType.includes('application/json') ? response.json() : { message: await response.text() };
  };

  const fetchLoopBundle = (bundleId, locale) => {
    const cacheKey = `${bundleId}|${locale}`;
    if (bundleCache.has(cacheKey)) return bundleCache.get(cacheKey);

    const url = `${LOOP_API}/${encodeURIComponent(bundleId)}?${new URLSearchParams({ locale })}`;
    const request = fetch(url, { headers: { Accept: 'application/json', 'Accept-Language': locale } }).then(
      async (response) => {
        const payload = await readJson(response);
        if (!response.ok) throw new Error(payload?.message || `Loop bundle API failed with ${response.status}`);
        const bundle = payload?.data ? payload.data.bundle ?? payload.data : payload;
        if (!bundle) throw new Error('Loop bundle data is missing.');
        return bundle;
      }
    );

    bundleCache.set(cacheKey, request);
    request.catch(() => bundleCache.delete(cacheKey));
    return request;
  };

  class LpBuyBox extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleAnchorClick = this.handleAnchorClick.bind(this);
    }

    connectedCallback() {
      const configEl = this.querySelector('[data-buy-box-config]');
      if (!configEl) return;

      this.config = JSON.parse(configEl.textContent);
      this.strings = this.config.strings;
      this.numberFormat = new Intl.NumberFormat(this.config.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: false
      });
      this.cards = Object.fromEntries(
        PURCHASE_TYPES.map((type) => [type, this.querySelector(`[data-purchase-card="${type}"]`)])
      );
      this.bundle = null;
      this.products = [];
      this.maxTotal = 3;
      this.sellingPlanId = null;
      this.selectedPurchaseType = 'subscription';
      this.state = Object.fromEntries(
        PURCHASE_TYPES.map((type) => [
          type,
          { quantities: {}, targetTier: 1, loading: false, success: false, errorMessage: '' }
        ])
      );

      this.addEventListener('click', this.handleClick);
      document.addEventListener('click', this.handleAnchorClick);
      this.render();
      this.load();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('click', this.handleAnchorClick);
    }

    async load() {
      try {
        this.bundle = await fetchLoopBundle(this.config.bundleId, this.config.locale);
        this.products = this.resolveProducts();
        if (!this.products.length) throw new Error('None of the configured flavours are part of the Loop bundle.');
        this.maxTotal = this.resolveMaxTotal();
        this.sellingPlanId = this.defaultSellingPlanId();
        this.preselectDefaultFlavour();
        this.hideUnavailableRows();
        this.render();
      } catch (error) {
        console.error('[lp-buy-box] Unable to load bundle data.', error);
        PURCHASE_TYPES.forEach((type) => this.setError(type, this.strings.errorLoad));
      }
    }

    resolveProducts() {
      const loopProducts = new Map(
        (this.bundle?.products ?? []).map((product) => [Number(product.shopifyId ?? product.shopifyProductId), product])
      );

      return this.config.flavours
        .filter((flavour) => loopProducts.has(flavour.id))
        .map((flavour) => {
          const loopProduct = loopProducts.get(flavour.id);
          const variant = loopProduct.variants?.[0] ?? {};
          return {
            id: String(flavour.id),
            title: flavour.title,
            image: flavour.image,
            variantId: Number(variant.shopifyId),
            productVariantId: variant.productVariantId,
            priceCents: Math.round(Number(variant.price ?? 0) * 100),
            limits: Array.isArray(loopProduct.limits) ? loopProduct.limits : []
          };
        });
    }

    hideUnavailableRows() {
      const available = new Set(this.products.map((product) => product.id));
      this.querySelectorAll('[data-flavour-row]').forEach((row) => {
        row.hidden = !available.has(row.dataset.flavourRow);
      });
    }

    preselectDefaultFlavour() {
      const target =
        this.products.find((product) => product.id === String(this.config.defaultFlavourId)) ?? this.products[0];
      if (!target) return;
      this.state.subscription.quantities = { [target.id]: 1 };
      this.state.subscription.targetTier = 1;
    }

    resolveMaxTotal() {
      const configuredMax = (this.bundle?.boxSizes ?? []).reduce(
        (max, boxSize) => Math.max(max, Number(boxSize.maxSize ?? 0)),
        0
      );
      return configuredMax > 0 ? configuredMax : 3;
    }

    defaultSellingPlanId() {
      const plans = this.bundle?.sellingPlans ?? [];
      const plan = plans.find((item) => Number(item.isDefault) === 1) ?? plans[0];
      return plan ? Number(plan.shopifyId ?? plan.id) : null;
    }

    totalQuantity(purchaseType) {
      const { quantities } = this.state[purchaseType];
      return this.products.reduce((total, product) => total + (quantities[product.id] ?? 0), 0);
    }

    setQuantity(purchaseType, productId, nextValue) {
      const state = this.state[purchaseType];
      const currentValue = state.quantities[productId] ?? 0;
      const currentTotal = this.totalQuantity(purchaseType);
      const safeValue = Math.max(0, nextValue);
      const proposedTotal = currentTotal - currentValue + safeValue;

      state.quantities[productId] =
        proposedTotal > this.maxTotal ? Math.max(0, currentValue + (this.maxTotal - currentTotal)) : safeValue;

      state.targetTier = Math.max(this.totalQuantity(purchaseType), 1);
      state.errorMessage = '';
      this.render();
    }

    selectTier(purchaseType, pots) {
      const state = this.state[purchaseType];
      if (this.totalQuantity(purchaseType) > pots) state.quantities = {};
      state.targetTier = pots;
      state.errorMessage = '';
      this.render();
    }

    selectPurchaseType(purchaseType) {
      if (this.selectedPurchaseType === purchaseType) return;
      this.selectedPurchaseType = purchaseType;
      this.render();
    }

    activeTier(purchaseType) {
      const total = this.totalQuantity(purchaseType);
      return Math.min(Math.max(this.state[purchaseType].targetTier, total), this.maxTotal);
    }

    unitPriceCents() {
      if (!this.products.length) return 0;
      return Math.round(this.products.reduce((sum, product) => sum + product.priceCents, 0) / this.products.length);
    }

    bundleDiscount(totalQty, purchaseType) {
      const matchesType = (discount) => {
        const type = String(discount.purchaseType ?? 'ALL').toUpperCase();
        if (type === 'ALL') return true;
        if (type === 'SUBSCRIPTION') return purchaseType === 'subscription';
        if (type === 'ONETIME') return purchaseType === 'one-time';
        return false;
      };

      return (
        (this.bundle?.discounts ?? [])
          .filter(matchesType)
          .sort((a, b) => Number(b.minCartQuantity ?? 0) - Number(a.minCartQuantity ?? 0))
          .find((discount) => totalQty >= Number(discount.minCartQuantity ?? 0)) ?? null
      );
    }

    bundleDiscountCents(baseSubtotal, totalQty, purchaseType) {
      const discount = this.bundleDiscount(totalQty, purchaseType);
      if (!discount || baseSubtotal <= 0) return 0;
      const value = Number(discount.value ?? 0);
      return String(discount.type ?? '').toUpperCase() === 'PERCENTAGE'
        ? Math.round((baseSubtotal * value) / 100)
        : Math.round(value * 100);
    }

    sellingPlanPercent() {
      const plans = this.bundle?.sellingPlans ?? [];
      const plan = plans.find((item) => Number(item.shopifyId ?? item.id) === this.sellingPlanId) ?? plans[0];
      if (!plan) return 0;

      const directDiscount = plan.discounts?.[0];
      if (directDiscount?.type === 'PERCENTAGE') return Number(directDiscount.value ?? 0);
      return Number(plan.pricingPolicies?.[0]?.adjustmentValue?.percentage ?? 0);
    }

    price(baseSubtotal, totalQty, purchaseType) {
      if (!totalQty || baseSubtotal <= 0) return { currentCents: 0, compareCents: 0 };

      const bundleCents = this.bundleDiscountCents(baseSubtotal, totalQty, purchaseType);
      const planCents =
        purchaseType === 'subscription' ? Math.round((baseSubtotal * this.sellingPlanPercent()) / 100) : 0;

      return { currentCents: Math.max(0, baseSubtotal - bundleCents - planCents), compareCents: baseSubtotal };
    }

    priceForTier(pots, purchaseType) {
      return this.price(this.unitPriceCents() * pots, pots, purchaseType);
    }

    priceForSelection(purchaseType) {
      const { quantities } = this.state[purchaseType];
      const subtotal = this.products.reduce(
        (total, product) => total + product.priceCents * (quantities[product.id] ?? 0),
        0
      );
      return this.price(subtotal, this.totalQuantity(purchaseType), purchaseType);
    }

    formatMoney(cents) {
      return `${this.config.currencySymbol}${this.numberFormat.format(Math.round(cents) / 100)}`;
    }

    setError(purchaseType, message) {
      this.state[purchaseType].errorMessage = message;
      this.render();
    }

    render() {
      PURCHASE_TYPES.forEach((purchaseType) => {
        const card = this.cards[purchaseType];
        if (!card) return;

        this.renderTiles(card, purchaseType);
        this.renderFlavours(card, purchaseType);
        this.renderHero(card, purchaseType);
        this.renderSummary(card, purchaseType);
        this.renderCta(card, purchaseType);

        const errorEl = card.querySelector('[data-buy-box-error]');
        if (errorEl) {
          const message = this.state[purchaseType].errorMessage;
          errorEl.textContent = message;
          errorEl.hidden = !message;
        }
      });

      this.classList.toggle('is-loading', !this.products.length);
    }

    renderTiles(card, purchaseType) {
      const activeTier = this.activeTier(purchaseType);
      card.querySelectorAll('[data-tier-button]').forEach((button) => {
        const isActive = activeTier === Number(button.dataset.tierButton);
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    renderFlavours(card, purchaseType) {
      const { quantities } = this.state[purchaseType];
      const atMax = this.totalQuantity(purchaseType) >= this.maxTotal;
      const disabled = atMax ? ' disabled' : '';

      card.querySelectorAll('[data-flavour-row]').forEach((row) => {
        const id = row.dataset.flavourRow;
        const qty = quantities[id] ?? 0;
        const qtyEl = row.querySelector('[data-flavour-qty]');
        row.classList.toggle('is-active', qty > 0);
        if (!qtyEl) return;

        if (!this.bundle) {
          qtyEl.innerHTML = '';
          return;
        }

        qtyEl.innerHTML =
          qty > 0
            ? `<div class="lp-buy-box__stepper">
                <button type="button" class="lp-buy-box__stepper-btn" data-qty-minus="${id}" aria-label="${escapeHtml(this.strings.decreaseQuantity)}">&minus;</button>
                <span class="lp-buy-box__stepper-value">${qty}</span>
                <button type="button" class="lp-buy-box__stepper-btn" data-qty-plus="${id}" aria-label="${escapeHtml(this.strings.increaseQuantity)}"${disabled}>+</button>
              </div>`
            : `<button type="button" class="lp-buy-box__add-btn" data-qty-add="${id}"${disabled}>${escapeHtml(this.strings.addPlus)}</button>`;
      });
    }

    renderHero(card, purchaseType) {
      const container = card.querySelector('[data-buy-box-hero]');
      if (!container) return;

      const placeholder = '<div class="lp-buy-box__hero-placeholder"><span aria-hidden="true">+</span></div>';
      const slotCount = this.activeTier(purchaseType);
      const { quantities } = this.state[purchaseType];
      const filled = this.products.flatMap((product) => Array(quantities[product.id] ?? 0).fill(product)).slice(0, slotCount);

      const filledHtml = filled
        .map((product) =>
          product.image
            ? `<img class="lp-buy-box__hero-image" src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="116" height="116">`
            : placeholder
        )
        .join('');

      container.innerHTML = filledHtml + placeholder.repeat(Math.max(0, slotCount - filled.length));
    }

    renderSummary(card, purchaseType) {
      const summary = this.priceForSelection(purchaseType);
      const isSelected = this.selectedPurchaseType === purchaseType;
      const selectEl = card.querySelector('[data-purchase-select]');
      const titleEl = card.querySelector('[data-card-title]');
      const priceEl = card.querySelector('[data-card-price]');
      const compareEl = card.querySelector('[data-card-compare]');

      card.classList.toggle('is-selected', isSelected);
      selectEl?.setAttribute('aria-pressed', String(isSelected));

      if (titleEl && purchaseType === 'subscription') {
        const percent = savingsPercent(summary);
        titleEl.textContent =
          percent > 0 ? interpolate(this.strings.subscribeTitle, { percent }) : this.strings.subscribeTitlePlain;
      }

      if (!this.bundle) return;

      const displayed =
        summary.currentCents > 0 ? summary : this.priceForTier(this.activeTier(purchaseType), purchaseType);
      if (priceEl) priceEl.textContent = this.formatMoney(displayed.currentCents);
      if (compareEl) {
        const showCompare = summary.compareCents > summary.currentCents;
        compareEl.textContent = showCompare ? this.formatMoney(summary.compareCents) : '';
        compareEl.hidden = !showCompare;
      }
    }

    renderCta(card, purchaseType) {
      const button = card.querySelector('[data-buy-box-atc]');
      if (!button) return;

      const state = this.state[purchaseType];
      const summary = this.priceForSelection(purchaseType);
      const hasSelection = this.totalQuantity(purchaseType) > 0;
      const priceEl = button.querySelector('[data-cta-price]');
      const compareEl = button.querySelector('[data-cta-compare]');

      if (priceEl) priceEl.textContent = hasSelection && summary.currentCents > 0 ? this.formatMoney(summary.currentCents) : '';
      if (compareEl) {
        const showCompare = summary.compareCents > summary.currentCents;
        compareEl.textContent = showCompare ? this.formatMoney(summary.compareCents) : '';
        compareEl.hidden = !showCompare;
      }

      button.disabled = !hasSelection || state.loading || !this.bundle;
      button.classList.toggle('is-loading', state.loading);
      button.classList.toggle('is-success', state.success);

      if (state.success) {
        button.setAttribute('aria-label', this.strings.addedToCart);
      } else if (state.loading) {
        button.setAttribute('aria-label', this.strings.addingToCart);
      } else {
        button.removeAttribute('aria-label');
      }
    }

    handleAnchorClick(event) {
      const link = event.target.closest('a[href]');
      if (!link || link.getAttribute('href') !== `#${this.id}`) return;
      event.preventDefault();
      this.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
    }

    handleClick(event) {
      const purchaseType = event.target.closest('[data-purchase-card]')?.dataset.purchaseCard;
      if (!purchaseType) return;

      const { target } = event;
      const quantities = this.state[purchaseType].quantities;
      const addBtn = target.closest('[data-qty-add]');
      const plusBtn = target.closest('[data-qty-plus]');
      const minusBtn = target.closest('[data-qty-minus]');
      const tierBtn = target.closest('[data-tier-button]');

      if (target.closest('[data-purchase-select]')) {
        this.selectPurchaseType(purchaseType);
      } else if (addBtn) {
        this.setQuantity(purchaseType, addBtn.dataset.qtyAdd, 1);
      } else if (plusBtn) {
        const id = plusBtn.dataset.qtyPlus;
        this.setQuantity(purchaseType, id, (quantities[id] ?? 0) + 1);
      } else if (minusBtn) {
        const id = minusBtn.dataset.qtyMinus;
        this.setQuantity(purchaseType, id, (quantities[id] ?? 0) - 1);
      } else if (tierBtn) {
        this.selectTier(purchaseType, Number(tierBtn.dataset.tierButton));
      } else if (target.closest('[data-buy-box-atc]')) {
        this.addToCart(purchaseType);
      }
    }

    selectedProducts(purchaseType) {
      const { quantities } = this.state[purchaseType];
      return this.products
        .map((product) => ({ ...product, quantity: quantities[product.id] ?? 0 }))
        .filter((product) => product.quantity > 0);
    }

    resolveBoxSizeId(selectedProducts) {
      const limit = selectedProducts.flatMap((product) => product.limits).find((item) => item?.boxSizeId);
      return limit ? String(limit.boxSizeId) : null;
    }

    async createTransaction(selectedProducts, totalQty, discount, sellingPlanId) {
      const response = await fetch(`${LOOP_API}/${encodeURIComponent(this.config.bundleId)}/transaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          boxSizeId: this.resolveBoxSizeId(selectedProducts),
          discountId: discount ? String(discount.id ?? discount.bundleDiscountId ?? discount.nanoId) : null,
          sellingPlanShopifyId: sellingPlanId,
          productVariants: selectedProducts.map((product) => ({
            shopifyId: product.variantId,
            quantity: product.quantity,
            ...(product.productVariantId ? { productVariantId: product.productVariantId } : {})
          })),
          quantity: totalQty
        })
      });
      const data = await readJson(response);
      const txnId = data?.data
        ? data.data.loopBundleGuid ?? data.data.txnId ?? data.data.bundleTransactionId
        : data?.txnId;

      if (!response.ok || !txnId) throw new Error(data?.message || this.strings.errorTransaction);
      return txnId;
    }

    async addItemsToCart(selectedProducts, txnId, sellingPlanId) {
      const items = selectedProducts.map((product) => ({
        id: product.variantId,
        quantity: product.quantity,
        properties: { _bundleId: txnId, _bundleName: this.bundle.name },
        ...(sellingPlanId ? { selling_plan: sellingPlanId } : {})
      }));

      const response = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await readJson(response);
      if (!response.ok || data.status === 422) {
        throw new Error(data.description || data.message || this.strings.errorAddToCart);
      }
    }

    async applyDiscountCode(discount) {
      if (!discount?.code) return;
      try {
        await fetch(`${routeRoot()}discount/${encodeURIComponent(discount.code)}`, { credentials: 'same-origin' });
      } catch (error) {
        console.warn('[lp-buy-box] Unable to apply the bundle discount code.', error);
      }
    }

    async openCartDrawer() {
      const cartHelper = window.Alpine?.store('xCartHelper');
      const miniCart = window.Alpine?.store('xMiniCart');
      if (!cartHelper || !miniCart) {
        window.location.href = `${routeRoot()}cart`;
        return;
      }

      const initStore = window.Alpine.store('xInit');
      if (initStore?.runDispatch) {
        initStore.runDispatch = false;
        window.dispatchEvent(new CustomEvent('init-run'));
      }

      miniCart.loading = true;
      miniCart.openCart();

      try {
        const cart = await fetch(`${routeRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            sections: cartHelper.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname
          })
        }).then((response) => response.json());

        window.Loop = window.Loop || {};
        window.Loop.bundleCartAllItems = cart.items ?? [];

        if (cart.sections) {
          cartHelper.reRenderSections(cart.sections);
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) cartHelper.currentItemCount = parseInt(bubble.innerHTML, 10);
          document.dispatchEvent(new CustomEvent('eurus:cart-drawer:rerender'));
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }

        if (typeof window.initLoopBundle === 'function' && window.Shopify?.shop) {
          try {
            window.initLoopBundle(`LOOP_${window.Shopify.shop.replace('.myshopify.com', '')}_bundles_drawer`);
          } catch (error) {
            console.warn('[lp-buy-box] Unable to refresh the Loop bundle drawer.', error);
          }
        }
      } catch (error) {
        console.error('[lp-buy-box] Unable to refresh the cart drawer.', error);
        miniCart.reLoad?.();
      } finally {
        miniCart.loading = false;
        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      }
    }

    async addToCart(purchaseType) {
      const state = this.state[purchaseType];
      const totalQty = this.totalQuantity(purchaseType);
      if (state.loading || !this.bundle || totalQty < 1) return;

      const selectedProducts = this.selectedProducts(purchaseType);
      if (!this.resolveBoxSizeId(selectedProducts)) {
        this.setError(purchaseType, this.strings.errorSelectFlavour);
        return;
      }

      const discount = this.bundleDiscount(totalQty, purchaseType);
      const sellingPlanId = purchaseType === 'subscription' ? this.sellingPlanId : null;

      state.loading = true;
      state.success = false;
      state.errorMessage = '';
      this.render();

      try {
        const txnId = await this.createTransaction(selectedProducts, totalQty, discount, sellingPlanId);
        await this.addItemsToCart(selectedProducts, txnId, sellingPlanId);
        await this.applyDiscountCode(discount);

        state.loading = false;
        state.success = true;
        this.render();
        await new Promise((resolve) => setTimeout(resolve, prefersReducedMotion() ? 0 : SUCCESS_DELAY_MS));

        if (this.config.afterAdd === 'cart_drawer') {
          state.success = false;
          this.render();
          await this.openCartDrawer();
        } else {
          window.location.href = `${routeRoot()}checkout`;
        }
      } catch (error) {
        console.error('[lp-buy-box] Add to cart failed.', error);
        state.loading = false;
        this.setError(purchaseType, error.message || this.strings.errorAddToCart);
      }
    }
  }

  if (!customElements.get('lp-buy-box')) {
    customElements.define('lp-buy-box', LpBuyBox);
  }
}
